import {
  ChangeEvent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { Card, Col, Container, Row } from "reactstrap";
import { Btn, H2 } from "../../../../AbstractElements";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  closeModal,
  openModal,
} from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { ModalType } from "../../../../Types/UserProfileDetails";
import {
  approvalAttendanceModal,
  bulkApprovedAttendanceTeamRequestList,
} from "../../../../utils/Constant";
import useUser from "../../../../hooks/useUser";
import Loader from "../../../../Layout/Loader/Loader";
import FinancialYearCycle from "../../../../CommonElements/YearPickerCycle";
import { setSelectedFinancialYear } from "../../../../ReduxToolkit/Slice/FinancialYearSlice";
import { Nullable } from "../../../../Types/Common";
import BulkActionBar from "../../../../CommonElements/BulkActionBar";
import { selectTeamMembersFinancialYear } from "../redux/selector/teamMembersAttendanceListSelector";
import TeamMembersAttendanceTable, {
  ISelectRows,
} from "./table/TeamMembersAttendanceTable";
import TeamMembersAttendanceCard from "./cards/TeamMembersAttendanceCard";
import TeamMembersAttendanceBulkApproveModal from "./modal/TeamMembersAttendanceBulkApproveModal";
import {
  CheckboxAttendanceRequest,
  IAttendanceRequestData,
  TeamMembersAttendanceStatusFilter,
} from "../types";
import { useModalSelector } from "../../../../hooks/useModalSelector";
import AttendanceExportReportDialog from "./attendanceExportDialog/";
import { logErrors } from "../../../../utils/logErrors";
import {
  teamMembersEndpointsApi,
  useTeamMembersAttendanceListQuery,
  useTeamMembersBulkApproveAttendanceMutation,
} from "../../../../ReduxToolkit/api/teamMembers";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  setResetFunctions,
  showEmployeeNotificationForm,
} from "../../../employeeManagement/redux/slice/EmployeeManagementSlice";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";

const TeamMembersAttendanceApproveModal = lazy(
  () => import("./modal/TeamMembersAttendanceApproveModal"),
);

const attendanceStatusFilterOptions: Array<{
  label: string;
  value: TeamMembersAttendanceStatusFilter;
}> = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const TeamMembersAttendance = () => {
  const location = useLocation();
  const user = useUser();
  const dispatch = useAppDispatch();

  const [filter, setFilter] = useState({
    text: "",
    searchEmployeeModal: null as Nullable<string>,
    selectAll: false,
  });

  const [attendanceRequests, setAttendanceRequests] = useState({
    all: [] as CheckboxAttendanceRequest[],
    filtered: [] as CheckboxAttendanceRequest[],
  });

  const [attendanceStatusFilter, setAttendanceStatusFilter] =
    useState<TeamMembersAttendanceStatusFilter>("pending");

  const [selection, setSelection] = useState({
    rows: null as Nullable<ISelectRows>,
    toggleCleared: false,
  });

  const { isOpen, modalType, selectedId } = useModalSelector();

  const { employeeProfileDetails, loggedInEmployee } = useEmployeeProfile();

  const employeeProfileId = employeeProfileDetails?.jobInformation?.userId;

  const isEmployeeProfilePath = location.pathname.includes(
    "/user-management/user",
  );

  const selectedFinancialYear = useAppSelector(selectTeamMembersFinancialYear);

  const financialYear = selectedFinancialYear?.split("-")[0];
  const isHrPage = location.pathname.includes("/hr-zone/employee-attendance");

  const shouldFetch =
    (employeeProfileId && financialYear) || (user?.id && financialYear);
  const {
    data: teamAttendanceData,
    isLoading: isTeamAttendanceLoading,
    isError: isTeamAttendanceError,
    error: teamAttendanceError,
    isFetching: isTeamAttendanceListFetching,
  } = useTeamMembersAttendanceListQuery(
    shouldFetch
      ? {
          user: isEmployeeProfilePath
            ? String(employeeProfileId)
            : String(user?.id),
          activeYear: Number(financialYear),
          ...(isHrPage ? { type: "hr" } : {}),
          status: attendanceStatusFilter,
        }
      : skipToken, // ensures query is skipped until user/year exist
  );

  const [
    updateTeamMembersBulkAttendanceCorrection,
    {
      isLoading: isBulkApproveAttendanceCorrectionLoading,
      reset: bulkApproveAttendanceCorrectionReset,
    },
  ] = useTeamMembersBulkApproveAttendanceMutation();

  const title = location.pathname.includes("/hr-zone/employee-attendance")
    ? "Employee's Attendance Correction Requests"
    : "Team Members Attendance Requests";

  // Keep bulk selection/actions available for all status filters,
  // same behavior as the Leave page.
  const canBulkApprove = ["pending", "approved", "rejected"].includes(
    attendanceStatusFilter,
  );

  const handleRowSelected = (state: ISelectRows) =>
    setSelection((prev) => ({ ...prev, rows: state }));

  const handleCloseModal = () => {
    setAttendanceRequests({ all: [], filtered: [] });
    setFilter({ text: "", searchEmployeeModal: null, selectAll: false });
    dispatch(closeModal());
  };

  const handleSetSelectedYear = (year: string) => {
    dispatch(
      setSelectedFinancialYear({ module: "teamMemberAttendance", year }),
    );
  };

  const handleStatusFilterChange = (
    status: TeamMembersAttendanceStatusFilter,
  ) => {
    setAttendanceStatusFilter(status);
    setFilter((prev) => ({ ...prev, text: "" }));
    setSelection((prev) => ({
      rows: null,
      toggleCleared: !prev.toggleCleared,
    }));
  };

  const statusFilterControl = (
    <div className="d-flex align-items-center gap-2">
      <label
        className="f-12 fw-semibold mb-0"
        htmlFor="attendance-status-filter"
      >
        Status
      </label>
      <select
        id="attendance-status-filter"
        className="form-select form-select-sm f-12"
        style={{ minWidth: 140 }}
        value={attendanceStatusFilter}
        onChange={(event) =>
          handleStatusFilterChange(
            event.target.value as TeamMembersAttendanceStatusFilter,
          )
        }
      >
        {attendanceStatusFilterOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  const sortEmployeeAttendanceRequestList = (
    a: CheckboxAttendanceRequest,
    b: CheckboxAttendanceRequest,
  ) => {
    if (a.isChecked !== b.isChecked) return a.isChecked ? -1 : 1;

    return a?.team_member?.name.localeCompare(b.team_member?.name);
  };

  const matchedAttendance = useMemo(() => {
    try {
      if (!teamAttendanceData || !Array.isArray(teamAttendanceData.data)) {
        logErrors("Attendance list is empty or invalid.");
        return;
      }
      if (
        selectedId === undefined ||
        selectedId === null ||
        isNaN(Number(selectedId))
      ) {
        logErrors("Selected ID is invalid.");
        return null;
      }
      const found = teamAttendanceData.data.find((attendance) => {
        const attendanceId = Number(attendance.attendance_request_id);
        if (isNaN(attendanceId)) {
          logErrors(
            "Invalid attendance_request_id:",
            attendance.attendance_request_id,
          );
          return false;
        }
        return attendanceId === Number(selectedId);
      });

      if (!found) {
        logErrors(`No attendance found for ID: ${selectedId}`);
      }

      return found;
    } catch (error: any) {
      logErrors("Error finding matched attendance:", error.message);
      return;
    }
  }, [teamAttendanceData, selectedId]);

  const handleReset = () => {
    bulkApproveAttendanceCorrectionReset();
    setSelection((prev) => ({
      rows: null,
      toggleCleared: !prev.toggleCleared,
    }));
    setAttendanceRequests({ all: [], filtered: [] });
    setFilter({ text: "", searchEmployeeModal: null, selectAll: false });
  };

  const handleBulkAttendance = async (action: "approved" | "rejected") => {
    try {
      const allRequests = attendanceRequests?.all ?? [];
      const checkedIds = allRequests
        .filter((d) => d.isChecked)
        .map((d) => d.attendance_request_id)
        .filter(Boolean);

      const selectedIds =
        selection?.rows?.selectedRows
          ?.map((d) => d.attendance_request_id)
          .filter(Boolean) ?? [];

      const ids = checkedIds.length ? checkedIds : selectedIds;

      if (!ids.length) {
        logErrors("No attendance request IDs selected for bulk action.");
        return;
      }

      const bulkApproveAttendanceCorrectionResponse =
        await updateTeamMembersBulkAttendanceCorrection({
          attendance_request_ids: ids,
          action,
        });

      showApiResultAlert(
        bulkApproveAttendanceCorrectionResponse,
        {
          title: `Bulk Attendance Correction Info`,
          source: "approvalAttendanceModal",
        },
        handleReset,
      );
    } catch (error) {
      logErrors("Error handling bulk attendance:", error);
    }
  };

  const handleOpenModal = (
    modalKey: ModalType,
    id?: string | number | null,
  ) => {
    try {
      if (!modalKey) {
        logErrors("Modal key is required to open a modal.");
        return;
      }

      // Ensure id is either null, undefined, string, or number
      const selectedId = id ?? null;

      dispatch(openModal({ modalType: modalKey, selectedId }));
    } catch (error) {
      logErrors("Error opening modal:", error);
    }
  };

  const handleOpenTeamAttendanceRequestModal = useCallback(() => {
    try {
      if (!teamAttendanceData?.data) {
        logErrors("Team members attendance list is missing or invalid.");
      }

      const list = (teamAttendanceData?.data ?? []).map(
        (d: IAttendanceRequestData) => ({ ...d, isChecked: false }),
      );

      setAttendanceRequests({ all: list, filtered: list });

      dispatch(openModal({ modalType: bulkApprovedAttendanceTeamRequestList }));
    } catch (error) {
      logErrors(
        "Error opening team attendance request modal:",
        error instanceof Error ? error.message : error,
      );
    }
  }, [teamAttendanceData, dispatch]);

  const handleCheckboxAttendanceRequest = useCallback(
    (e: ChangeEvent<HTMLInputElement>, item: CheckboxAttendanceRequest) => {
      try {
        if (
          !attendanceRequests?.all ||
          !Array.isArray(attendanceRequests.all)
        ) {
          logErrors("Attendance requests list is invalid or missing.");
          return;
        }

        if (!item?.attendance_request_id) {
          logErrors("Invalid attendance request item:", item);
          return;
        }

        const isChecked = e?.target?.checked ?? false;

        const updated = attendanceRequests.all.map((req) =>
          req.attendance_request_id === item.attendance_request_id
            ? { ...req, isChecked }
            : req,
        );

        const filtered = updated.filter((req) => {
          const name = req.team_member?.name ?? "";
          const searchText = filter?.searchEmployeeModal ?? "";
          return (
            !searchText || name.toLowerCase().includes(searchText.toLowerCase())
          );
        });

        setAttendanceRequests({ all: updated, filtered });
      } catch (error) {
        logErrors(
          "Error in handleCheckboxAttendanceRequest:",
          error instanceof Error ? error.message : error,
        );
      }
    },
    [attendanceRequests, filter],
  );

  const handleSelectAllAttendanceRequest = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      try {
        const checked = e?.target?.checked ?? false;

        const matchesSearch = (name: string = "") =>
          !filter?.searchEmployeeModal ||
          name
            .toLowerCase()
            .replaceAll(" ", "")
            .includes(
              filter.searchEmployeeModal.toLowerCase().replaceAll(" ", ""),
            );

        const updated = (attendanceRequests?.all ?? []).map((item) =>
          matchesSearch(item.team_member?.name ?? "")
            ? { ...item, isChecked: checked }
            : item,
        );

        setAttendanceRequests({
          all: updated,
          filtered: updated.filter((item) =>
            matchesSearch(item.team_member?.name ?? ""),
          ),
        });

        setFilter((prev) => ({ ...prev, selectAll: checked }));
      } catch (error) {
        logErrors(
          "Error in handleSelectAllAttendanceRequest:",
          error instanceof Error ? error.message : error,
        );
      }
    },
    [attendanceRequests, filter],
  );

  const handleSearchEmployeeAttendanceRequest = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      try {
        const value = e?.target?.value?.trim() ?? "";

        const filtered = value
          ? (attendanceRequests?.all ?? []).filter((item) =>
              item.team_member?.name
                ?.toLowerCase()
                .includes(value.toLowerCase()),
            )
          : (attendanceRequests?.all ?? []);

        setAttendanceRequests((prev) => ({ ...prev, filtered }));
        setFilter((prev) => ({
          ...prev,
          searchEmployeeModal: value,
          selectAll: false,
        }));
      } catch (error) {
        logErrors(
          "Error in handleSearchEmployeeAttendanceRequest:",
          error instanceof Error ? error.message : error,
        );
      }
    },
    [attendanceRequests],
  );

  useEffect(() => {
    if (isTeamAttendanceError) {
      showApiResultAlert(teamAttendanceError, {
        title: `Hey ${loggedInEmployee?.name}!`,
      });

      const safeMessage =
        (teamAttendanceError as any)?.data?.message ||
        (teamAttendanceError as any)?.error ||
        "Something went wrong while fetching team attendance data.";
      dispatch(
        setResetFunctions({
          queryReset: () =>
            dispatch(
              teamMembersEndpointsApi.util.invalidateTags(["TeamMembersInfo"]),
            ),
        }),
      );
      dispatch(
        showEmployeeNotificationForm({
          message: safeMessage,
          type: "warning",
          title: "",
          source: "",
        }),
      );
    }
  }, [isTeamAttendanceError]);

  return (
    <Container fluid className="pt-3">
      <Row>
        <Col>
          <Card className="mb-0 mt-2 mt-lg-4 mb-lg-3 px-2 pt-3 pb-3">
            <div className="d-lg-flex justify-content-lg-between align-items-lg-center mb-lg-4">
              <H2 className="f-16 mb-3 mb-lg-0">{title}</H2>
              <div className="d-none d-lg-flex flex-wrap align-items-center justify-content-end gap-3">
                {statusFilterControl}
                <FinancialYearCycle
                  selectedYear={selectedFinancialYear ?? ""}
                  setSelectedYear={handleSetSelectedYear}
                  startMonth={9} // October (0-based index)
                  startDateValue={1}
                  endMonth={8} // September (0-based index)
                  endDateValue={30}
                />
              </div>
              <Row className="d-lg-none align-items-center">
                <Col xs="12" className="mb-3">
                  {statusFilterControl}
                </Col>
                <Col xs={canBulkApprove ? "6" : "12"}>
                  <FinancialYearCycle
                    selectedYear={selectedFinancialYear ?? ""}
                    setSelectedYear={handleSetSelectedYear}
                    startMonth={9} // October (0-based index)
                    startDateValue={1}
                    endMonth={8} // September (0-based index)
                    endDateValue={30}
                  />
                </Col>
                {canBulkApprove && (
                  <Col xs="6">
                    <Btn
                      color="primary"
                      className="f-12 w-100"
                      onClick={handleOpenTeamAttendanceRequestModal}
                    >
                      Approve/Reject All
                    </Btn>
                  </Col>
                )}
              </Row>
            </div>
            <div className="d-none d-lg-block table-responsive user-datatable custom-scrollbar mt-2 position-relative">
              <TeamMembersAttendanceTable
                dispatch={dispatch}
                toggleCleared={selection.toggleCleared}
                onRowSelected={handleRowSelected}
                handleOpenModal={handleOpenModal}
                filterText={filter.text}
                setFilterText={(text) =>
                  setFilter((prev) => ({ ...prev, text }))
                }
                teamMembersAttendanceList={teamAttendanceData?.data || []}
                teamMemberAttendanceListLoading={
                  isTeamAttendanceLoading || isTeamAttendanceListFetching
                }
                location={location}
              />
              {canBulkApprove &&
                selection.rows &&
                selection.rows.selectedRows?.length > 0 && (
                  <BulkActionBar
                    label={`You have selected: ${selection.rows.selectedRows.length}`}
                    selectedRows={selection.rows.selectedRows}
                    onApprove={() => handleBulkAttendance("approved")}
                    onReject={() => handleBulkAttendance("rejected")}
                  />
                )}
            </div>
          </Card>
        </Col>
      </Row>
      <div className="d-block d-lg-none">
        <Row className="pt-3">
          {teamAttendanceData &&
            Array.isArray(teamAttendanceData?.data) &&
            teamAttendanceData.data.length > 0 &&
            teamAttendanceData?.data?.map((teamMember, i) => (
              <TeamMembersAttendanceCard
                index={i}
                openModal={handleOpenModal}
                teamMembersAttendance={teamMember}
                teamMemberAttendanceLoading={
                  isTeamAttendanceLoading ||
                  isBulkApproveAttendanceCorrectionLoading
                }
              />
            ))}
        </Row>
      </div>

      {modalType === approvalAttendanceModal && matchedAttendance && isOpen && (
        <Suspense fallback={<Loader />}>
          <TeamMembersAttendanceApproveModal
            isOpen={isOpen}
            modalType={modalType}
            closeModal={handleCloseModal}
            matchedAttendance={matchedAttendance}
            attendanceStatusFilter={attendanceStatusFilter}
          />
        </Suspense>
      )}
      {modalType === bulkApprovedAttendanceTeamRequestList && (
        <Suspense fallback={<Loader />}>
          <TeamMembersAttendanceBulkApproveModal
            isOpen={isOpen}
            closeModal={handleCloseModal}
            allTeamAttendanceRequests={attendanceRequests.all}
            checkboxTeamAttendanceRequest={attendanceRequests.filtered}
            onBulkAttendance={handleBulkAttendance}
            searchEmployeeAttendanceRequest={
              handleSearchEmployeeAttendanceRequest
            }
            onCheckboxAttendanceRequest={handleCheckboxAttendanceRequest}
            onSelectAllAttendanceRequest={handleSelectAllAttendanceRequest}
            selectAllAttendanceRequest={filter.selectAll}
            sortEmployeeAttendanceRequestList={
              sortEmployeeAttendanceRequestList
            }
          />
        </Suspense>
      )}
      <AttendanceExportReportDialog
        dispatch={dispatch}
        modalType={modalType}
        isOpen={isOpen}
      />
    </Container>
  );
};

export default TeamMembersAttendance;
